import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import { UserCog, Plus, Edit, Trash2, Save, X, Check } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const ConfiguracoesPerfil = () => {
  const { user } = useAuth();
  const { getCondominioId } = useCondominio();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfis, setPerfis] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState(null);
  const [formData, setFormData] = useState({
    perfil_nome: '',
    perfil_descricao: '',
    perfil_permissoes: {
      dashboard: true,
      condominios: false,
      apartamentos: false,
      encomendas: false,
      visitantes: false,
      comunicados: false,
      agenda: false,
      reservas: false,
      ocorrencias: false,
      moradores: false,
      pets: false,
      veiculos: false,
      usuarios: false,
      funcionarios: false,
      areas_comuns: false,
      achados_perdidos: false,
      documentos: false,
      relatorios: false,
      configuracoes: false
    }
  });

  // Definição das permissões disponíveis
  const permissoesDisponiveis = [
    { key: 'dashboard', label: 'Dashboard', description: 'Acesso ao painel principal' },
    { key: 'condominios', label: 'Condomínios', description: 'Gerenciar condomínios' },
    { key: 'apartamentos', label: 'Apartamentos', description: 'Gerenciar apartamentos' },
    { key: 'encomendas', label: 'Encomendas', description: 'Gerenciar encomendas' },
    { key: 'visitantes', label: 'Visitantes', description: 'Gerenciar visitantes' },
    { key: 'comunicados', label: 'Comunicados', description: 'Gerenciar comunicados' },
    { key: 'agenda', label: 'Agenda do Condomínio', description: 'Visualizar agenda' },
    { key: 'reservas', label: 'Reserva de Espaço', description: 'Fazer reservas' },
    { key: 'ocorrencias', label: 'Ocorrências', description: 'Visualizar ocorrências' },
    { key: 'moradores', label: 'Moradores', description: 'Gerenciar moradores' },
    { key: 'pets', label: 'Pets', description: 'Gerenciar pets' },
    { key: 'veiculos', label: 'Veículos', description: 'Gerenciar veículos' },
    { key: 'usuarios', label: 'Usuários', description: 'Gerenciar usuários' },
    { key: 'funcionarios', label: 'Funcionários', description: 'Gerenciar funcionários' },
    { key: 'areas_comuns', label: 'Áreas Comuns', description: 'Gerenciar áreas comuns' },
    { key: 'achados_perdidos', label: 'Achados e Perdidos', description: 'Visualizar achados e perdidos' },
    { key: 'documentos', label: 'Documentos', description: 'Visualizar documentos' },
    { key: 'relatorios', label: 'Relatórios', description: 'Acessar relatórios' },
    { key: 'configuracoes', label: 'Configurações', description: 'Acessar configurações do sistema' }
  ];

  useEffect(() => {
    loadPerfis();
  }, []);

  const loadPerfis = async () => {
    try {
      setLoading(true);
      // Aqui você pode implementar a API para carregar perfis
      // Por enquanto, vamos usar dados de exemplo
      const perfisExemplo = [
        {
          id: 1,
          perfil_nome: 'Administrador',
          perfil_descricao: 'Acesso total ao sistema',
          perfil_permissoes: {
            dashboard: true,
            condominios: true,
            apartamentos: true,
            encomendas: true,
            visitantes: true,
            comunicados: true,
            agenda: true,
            reservas: true,
            ocorrencias: true,
            moradores: true,
            pets: true,
            veiculos: true,
            usuarios: true,
            funcionarios: true,
            areas_comuns: true,
            achados_perdidos: true,
            documentos: true,
            relatorios: true,
            configuracoes: true
          }
        },
        {
          id: 2,
          perfil_nome: 'Morador',
          perfil_descricao: 'Acesso limitado para moradores',
          perfil_permissoes: {
            dashboard: true,
            condominios: false,
            apartamentos: false,
            encomendas: true,
            visitantes: true,
            comunicados: true,
            agenda: true,
            reservas: true,
            ocorrencias: true,
            moradores: true,
            pets: true,
            veiculos: true,
            usuarios: false,
            funcionarios: false,
            areas_comuns: false,
            achados_perdidos: true,
            documentos: true,
            relatorios: false,
            configuracoes: false
          }
        },
        {
          id: 3,
          perfil_nome: 'Porteiro',
          perfil_descricao: 'Acesso para funcionários da portaria',
          perfil_permissoes: {
            dashboard: true,
            condominios: false,
            apartamentos: true,
            encomendas: true,
            visitantes: true,
            comunicados: false,
            agenda: false,
            reservas: false,
            ocorrencias: true,
            moradores: true,
            pets: true,
            veiculos: true,
            usuarios: false,
            funcionarios: false,
            areas_comuns: true,
            achados_perdidos: true,
            documentos: true,
            relatorios: false,
            configuracoes: false
          }
        }
      ];
      setPerfis(perfisExemplo);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissaoChange = (permissaoKey, checked) => {
    setFormData(prev => ({
      ...prev,
      perfil_permissoes: {
        ...prev.perfil_permissoes,
        [permissaoKey]: checked
      }
    }));
  };

  const resetForm = () => {
    setFormData({
      perfil_nome: '',
      perfil_descricao: '',
      perfil_permissoes: {
        dashboard: true,
        condominios: false,
        apartamentos: false,
        encomendas: false,
        visitantes: false,
        comunicados: false,
        agenda: false,
        reservas: false,
        ocorrencias: false,
        moradores: false,
        pets: false,
        veiculos: false,
        usuarios: false,
        funcionarios: false,
        areas_comuns: false,
        achados_perdidos: false,
        documentos: false,
        relatorios: false,
        configuracoes: false
      }
    });
    setEditingPerfil(null);
  };

  const handleNewPerfil = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEditPerfil = (perfil) => {
    setFormData({
      perfil_nome: perfil.perfil_nome,
      perfil_descricao: perfil.perfil_descricao,
      perfil_permissoes: { ...perfil.perfil_permissoes }
    });
    setEditingPerfil(perfil);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (editingPerfil) {
        // Atualizar perfil existente
        const perfisAtualizados = perfis.map(p => 
          p.id === editingPerfil.id 
            ? { ...p, ...formData }
            : p
        );
        setPerfis(perfisAtualizados);
      } else {
        // Criar novo perfil
        const novoPerfil = {
          id: Date.now(), // ID temporário
          ...formData
        };
        setPerfis(prev => [...prev, novoPerfil]);
      }
      
      setShowModal(false);
      resetForm();
      
      // Aqui você pode implementar a API para salvar no backend
      console.log('Salvando perfil:', formData);
      
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      alert('Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePerfil = async (perfilId) => {
    if (window.confirm('Tem certeza que deseja excluir este perfil?')) {
      try {
        setPerfis(prev => prev.filter(p => p.id !== perfilId));
        // Aqui você pode implementar a API para excluir no backend
        console.log('Excluindo perfil:', perfilId);
      } catch (error) {
        console.error('Erro ao excluir perfil:', error);
        alert('Erro ao excluir perfil');
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <UserCog className="h-6 w-6 mr-2" />
          Configurações de Perfil
        </h1>
        <p className="text-gray-600 mt-1">
          Gerencie os perfis de usuário e suas permissões
        </p>
      </div>

      {/* Botão Novo Perfil */}
      <div className="mb-6">
        <button
          onClick={handleNewPerfil}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Novo Perfil
        </button>
      </div>

      {/* Lista de Perfis */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="space-y-4">
            {perfis.map((perfil) => (
              <div key={perfil.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {perfil.perfil_nome}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {perfil.perfil_descricao}
                    </p>
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Permissões:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(perfil.perfil_permissoes)
                          .filter(([_, hasPermission]) => hasPermission)
                          .map(([key, _]) => {
                            const permissao = permissoesDisponiveis.find(p => p.key === key);
                            return (
                              <span
                                key={key}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                {permissao?.label}
                              </span>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditPerfil(perfil)}
                      className="text-blue-600 hover:text-blue-900 p-1"
                      title="Editar perfil"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePerfil(perfil.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                      title="Excluir perfil"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de Edição/Criação */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingPerfil ? 'Editar Perfil' : 'Novo Perfil'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
                {/* Informações Básicas */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Perfil
                      </label>
                      <input
                        type="text"
                        name="perfil_nome"
                        value={formData.perfil_nome}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <input
                        type="text"
                        name="perfil_descricao"
                        value={formData.perfil_descricao}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Permissões */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Permissões</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {permissoesDisponiveis.map((permissao) => (
                      <div key={permissao.key} className="flex items-start">
                        <input
                          type="checkbox"
                          id={permissao.key}
                          checked={formData.perfil_permissoes[permissao.key] || false}
                          onChange={(e) => handlePermissaoChange(permissao.key, e.target.checked)}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <label htmlFor={permissao.key} className="text-sm font-medium text-gray-700">
                            {permissao.label}
                          </label>
                          <p className="text-xs text-gray-500">
                            {permissao.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Botões */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Salvando...' : 'Salvar Perfil'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguracoesPerfil;



